/**
 * SafeStorage – wraps every localStorage call in a try/catch so that
 * SecurityError (private/restricted mode) and QuotaExceededError (full storage)
 * never bubble up and cause a blank page.
 *
 * Usage: drop-in replacement for `localStorage` throughout the app.
 *   import { SafeStorage } from '@core/utils/safe-storage';
 *   SafeStorage.getItem('key')  → string | null
 *   SafeStorage.setItem('key', value)
 *   SafeStorage.removeItem('key')
 *   SafeStorage.clear()
 */
export class SafeStorage {
  static getItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  static setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // QuotaExceededError or SecurityError – silently ignore
    }
  }

  static removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // silently ignore
    }
  }

  static clear(): void {
    try {
      localStorage.clear();
    } catch {
      // silently ignore
    }
  }

  /**
   * Clears every key except those starting with one of the given prefixes.
   * Use this instead of clear() wherever app state resets happen on a page
   * that also carries third-party SDK state in the same localStorage — e.g.
   * PostHog persists its distinct_id under `ph_*`, and wiping it mid-session
   * severs the identity link between anonymous and identified events.
   */
  static clearExceptPrefixes(prefixes: string[]): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && !prefixes.some(prefix => key.startsWith(prefix))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch {
      // silently ignore
    }
  }
}
